import { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { trackPortfolioFilter, trackPortfolioItemClick } from '../utils/analytics';

const categories = [
  {
    key: 'weddings',
    label: 'Weddings',
    images: [
      { src: '/portfolio/drive_wedding_4.png', alt: 'Luxury destination wedding setup by CinCin Events' },
      { src: '/portfolio/drive_wedding_5.png', alt: 'Couple with sparklers at luxury wedding celebration' },
      { src: '/portfolio/drive_wedding_2.jpg', alt: 'Neon sign wedding decor by CinCin' },
      { src: '/portfolio/drive_wedding_8.jpg', alt: 'Dramatic red floral arch wedding design India' },
      { src: '/portfolio/drive_wedding_1.png', alt: 'Tropical floral installation wedding decor' },
      { src: '/portfolio/drive_wedding_3.jpg', alt: 'Premium wedding bar setup by CinCin Events' },
      { src: '/portfolio/drive_wedding_7.jpg', alt: 'Decorative wedding elements by CinCin' },
    ]
  },
  {
    key: 'corporate',
    label: 'Corporate Experiences',
    images: [
      { src: '/portfolio/drive_corporate_6.png', alt: 'Conference keynote speaker at CinCin corporate event', objectPos: '50% 20%' },
      { src: '/portfolio/drive_corporate_1.png', alt: 'Twamev luxury brand display executed by CinCin Events' },
      { src: '/portfolio/drive_corporate_2.png', alt: 'Foxtale brand launch event executed by CinCin Events' },
      { src: '/portfolio/drive_corporate_3.png', alt: 'Anglo Indian food experience executed by CinCin Events' },
      { src: '/portfolio/drive_corporate_4.png', alt: 'La Fervance corporate event executed by CinCin Events' },
      { src: '/portfolio/drive_corporate_5.png', alt: 'Live performance corporate event design by CinCin Events' },
      { src: '/portfolio/drive_corporate_new.jpg', alt: 'Corporate brand experience event executed by CinCin Events' },
    ]
  },
  {
    key: 'exhibitions',
    label: 'Exhibitions & Stalls',
    images: [
      { src: '/portfolio/drive_exhibition_hero.jpg', alt: 'Exhibition stall design India by CinCin Events' },
      { src: '/portfolio/drive_exhibition_1.png', alt: 'Brand exhibition stall design executed by CinCin Events' },
      { src: '/portfolio/drive_exhibition_2.png', alt: 'Exhibition setup and activation executed by CinCin Events' },
      { src: '/portfolio/drive_exhibition_3.png', alt: 'Exhibition display experience executed by CinCin Events' },
      { src: '/portfolio/drive_exhibition_replace.jpg', alt: 'Exhibition stall design executed by CinCin Events' },
      { src: '/portfolio/drive_exhibition_5.png', alt: 'Exhibition environment design executed by CinCin Events' },
      { src: '/portfolio/drive_exhibition_new.jpg', alt: 'Exhibition stall design executed by CinCin Events' },
    ]
  },
  {
    key: 'private',
    label: 'Private Celebrations',
    images: [
      { src: '/portfolio/drive_private_6.png', alt: 'Romantic proposal setup executed by CinCin Events' },
      { src: '/portfolio/drive_private_5.jpg', alt: 'Romantic roses arrangement by CinCin Events' },
      { src: '/portfolio/drive_private_replace.jpg', alt: 'Baby shower decor and styling executed by CinCin Events' },
      { src: '/portfolio/drive_private_2.png', alt: 'Welcome bar decoration for private celebration by CinCin Events' },
      { src: '/portfolio/drive_private_4.png', alt: 'Carnival birthday celebration executed by CinCin Events' },
      { src: '/portfolio/drive_private_3.jpg', alt: 'Private celebration experience by CinCin Events' },
      { src: '/portfolio/drive_private_new.jpg', alt: 'Private celebration decor and experience executed by CinCin Events' },
    ]
  }
];

export default function PortfolioSection() {
  const [activeCategory, setActiveCategory] = useState('weddings');
  const [lightbox, setLightbox] = useState({ open: false, categoryKey: '', index: 0 });
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  const currentCat = categories.find(c => c.key === activeCategory);
  const allImages = currentCat?.images || [];
  // Deduplicate by src
  const uniqueImages = allImages.filter(
    (img, idx, self) => idx === self.findIndex(i => i.src === img.src)
  );
  const heroImage = uniqueImages[0];
  const gridImages = uniqueImages.slice(1);

  const openLightbox = (index) => {
    setLightbox({ open: true, categoryKey: activeCategory, index });
    document.body.style.overflow = 'hidden';
    trackPortfolioItemClick(activeCategory);
  };

  const closeLightbox = () => {
    setLightbox({ ...lightbox, open: false });
    document.body.style.overflow = '';
  };

  const navigateLightbox = (dir) => {
    let next = lightbox.index + dir;
    if (next < 0) next = uniqueImages.length - 1;
    if (next >= uniqueImages.length) next = 0;
    setLightbox({ ...lightbox, index: next });
  };

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      ref={ref}
      className="bg-white py-20 md:py-36 px-6 md:px-16 lg:px-24"
    >
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <span className="label-text" data-testid="portfolio-label">Selected Work</span>
          <div className="editorial-line mt-4 mb-4" />
          <p className="font-serif text-sm sm:text-base italic text-[#0A0A0A]/40 mb-8">
            A glimpse into the experiences we've brought to life.
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A0A0A] leading-[1.05]">
            Portfolio
          </h2>
          <p className="mt-5 font-light text-sm sm:text-base text-[#0A0A0A]/50 max-w-lg tracking-wide">
            A curated selection of moments and brand experiences we've brought to life.
          </p>
        </motion.div>

        {/* Category tabs */}
        <div className="mt-12 md:mt-16 flex flex-wrap gap-4 md:gap-8" data-testid="portfolio-categories">
          {categories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => { setActiveCategory(cat.key); trackPortfolioFilter(cat.key); }}
              data-testid={`portfolio-tab-${cat.key}`}
              className={`text-[10px] md:text-xs tracking-[0.18em] uppercase font-light pb-2 transition-all duration-300 ${
                activeCategory === cat.key
                  ? 'text-[#0A0A0A] border-b border-[#0A0A0A]'
                  : 'text-[#8C8C8C] hover:text-[#0A0A0A]'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Structured grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-10"
            data-testid="portfolio-grid"
          >
            {/* Hero image — full width */}
            {heroImage && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="overflow-hidden cursor-pointer mb-[2px]"
                onClick={() => openLightbox(0)}
                data-testid={`portfolio-image-${activeCategory}-0`}
              >
                <div className="relative w-full aspect-[16/7] overflow-hidden">
                  <img
                    src={heroImage.src}
                    alt={heroImage.alt}
                    className="absolute inset-0 w-full h-full object-cover portfolio-image"
                    style={heroImage.objectPos ? { objectPosition: heroImage.objectPos } : {}}
                    loading="lazy"
                  />
                </div>
              </motion.div>
            )}

            {/* Grid images — strict 3-col desktop, 2-col tablet */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-[2px]">
              {gridImages.map((img, i) => (
                <motion.div
                  key={img.src}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: (i + 1) * 0.06 }}
                  className="overflow-hidden cursor-pointer"
                  onClick={() => openLightbox(i + 1)}
                  data-testid={`portfolio-image-${activeCategory}-${i + 1}`}
                >
                  <div className="relative w-full aspect-[4/3] overflow-hidden">
                    <img
                      src={img.src}
                      alt={img.alt}
                      className="absolute inset-0 w-full h-full object-cover portfolio-image"
                      style={img.objectPos ? { objectPosition: img.objectPos } : {}}
                      loading="lazy"
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 portfolio-modal-overlay flex items-center justify-center"
            onClick={closeLightbox}
            data-testid="portfolio-lightbox"
          >
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-10"
              data-testid="lightbox-close"
            >
              <X size={28} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
              className="absolute left-4 md:left-8 text-white/50 hover:text-white transition-colors z-10"
              data-testid="lightbox-prev"
            >
              <ChevronLeft size={36} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
              className="absolute right-4 md:right-8 text-white/50 hover:text-white transition-colors z-10"
              data-testid="lightbox-next"
            >
              <ChevronRight size={36} />
            </button>
            <motion.img
              key={lightbox.index}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              src={uniqueImages[lightbox.index]?.src}
              alt={uniqueImages[lightbox.index]?.alt}
              className="max-h-[85vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
